import React from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { SCOPES, type ScopeKey } from '../../../lib/rbac/permissions.generated';
import type { Katalog, RollenZuweisung } from '../../../lib/rbac/userAdmin';

const NO_OVERRIDE = '__none__';

/** ISO-Zeitpunkt ↔ Datumsfeld (lokaler Tagesbeginn). */
const toDateInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromDateInput = (value: string): string | null =>
  value ? new Date(`${value}T00:00:00`).toISOString() : null;

interface Props {
  katalog: Katalog;
  value: RollenZuweisung[];
  onChange: (next: RollenZuweisung[]) => void;
  disabled?: boolean;
}

/**
 * Rollenzuweisungen eines Benutzers. Jede Zeile ist eine Zuweisung mit eigenem Scope
 * (RBAC-SPEC A.4): dieselbe Rolle kann einmal unternehmensweit und einmal für
 * bestimmte Standorte zugewiesen sein.
 */
const RoleAssignmentEditor: React.FC<Props> = ({ katalog, value, onChange, disabled }) => {
  const roleByKey = new Map(katalog.rollen.map((r) => [r.key, r]));
  const scopeLabel = new Map(katalog.scopes.map((s) => [s.key, s.label]));

  const update = (index: number, patch: Partial<RollenZuweisung>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const add = () => {
    const used = new Set(value.map((r) => `${r.role_key}:${r.scope_type}`));
    const next = katalog.rollen.find((r) => !used.has(`${r.key}:global`));
    onChange([
      ...value,
      {
        role_key: next?.key ?? katalog.rollen[0]?.key ?? '',
        scope_type: 'global',
        standort_ids: [],
        scope_override: null,
        valid_from: null,
        valid_until: null,
      },
    ]);
  };

  return (
    <div className="space-y-3">
      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Keine Rollen — der Benutzer kann sich anmelden, sieht aber nichts.
        </p>
      )}
      {value.map((row, index) => {
        const role = roleByKey.get(row.role_key);
        const ceilingRank = role ? SCOPES.indexOf(role.scope_ceiling) : SCOPES.length - 1;
        const narrower = SCOPES.filter((s) => SCOPES.indexOf(s) < ceilingRank);
        return (
          <div key={row.id ?? `neu-${index}`} className="space-y-3 rounded-md border p-3">
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]">
              <div>
                <Label>Rolle</Label>
                <Select
                  value={row.role_key}
                  onValueChange={(v: string) => update(index, { role_key: v, scope_override: null })}
                  disabled={disabled}
                >
                  <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {katalog.rollen.map((r) => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Geltung</Label>
                <Select
                  value={row.scope_type}
                  onValueChange={(v: string) =>
                    update(index, {
                      scope_type: v as RollenZuweisung['scope_type'],
                      standort_ids: v === 'global' ? [] : row.standort_ids,
                    })
                  }
                  disabled={disabled}
                >
                  <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Ohne Standortbindung</SelectItem>
                    <SelectItem value="location">Bestimmte Standorte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scope begrenzen</Label>
                <Select
                  value={row.scope_override ?? NO_OVERRIDE}
                  onValueChange={(v: string) =>
                    update(index, { scope_override: v === NO_OVERRIDE ? null : (v as ScopeKey) })
                  }
                  disabled={disabled || narrower.length === 0}
                >
                  <SelectTrigger size="sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_OVERRIDE}>
                      Obergrenze der Rolle ({role ? scopeLabel.get(role.scope_ceiling) : '—'})
                    </SelectItem>
                    {narrower.map((s) => (
                      <SelectItem key={s} value={s}>{scopeLabel.get(s) ?? s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                  disabled={disabled}
                  aria-label="Zuweisung entfernen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {role?.team_rolle && (
              <p className="text-xs text-muted-foreground">
                Diese Rolle wirkt über die Team-Mitgliedschaften des Benutzers (Scope höchstens Team).
              </p>
            )}
            {role?.company_braucht_bereich && row.scope_type === 'global' && (
              <p className="text-xs text-muted-foreground">
                Unternehmensweit nur über zugewiesene Verantwortungsbereiche; ohne Bereich nur Team-Scope.
              </p>
            )}

            {row.scope_type === 'location' && (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {katalog.standorte.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={row.standort_ids.includes(s.id)}
                      disabled={disabled}
                      onCheckedChange={(checked) =>
                        update(index, {
                          standort_ids: checked
                            ? [...row.standort_ids, s.id]
                            : row.standort_ids.filter((id) => id !== s.id),
                        })
                      }
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Gültig ab (optional)</Label>
                <Input
                  type="date"
                  uiSize="sm"
                  value={toDateInput(row.valid_from)}
                  disabled={disabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    update(index, { valid_from: fromDateInput(e.target.value) })
                  }
                />
              </div>
              <div>
                <Label>Gültig bis (optional)</Label>
                <Input
                  type="date"
                  uiSize="sm"
                  value={toDateInput(row.valid_until)}
                  disabled={disabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    update(index, { valid_until: fromDateInput(e.target.value) })
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={add} disabled={disabled}>
        <PlusCircle className="mr-2 h-4 w-4" /> Rolle hinzufügen
      </Button>
    </div>
  );
};

export default RoleAssignmentEditor;
